import { apiAuth, apiResponseType } from "./index";

// Listar todas as solicitações (admin/gerente)
export const getSaleChangeRequests = (
  page = 1,
  params?: {
    unity_ids?: number[];
    status?: string[];
    type?: string[];
    requester_id?: number[];
    sale_id?: number;
    client_name?: string;
    start_date?: string;
    end_date?: string;
    order_by?: string;
    order_direction?: "asc" | "desc";
  }
): apiResponseType => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());

  if (params?.unity_ids && params.unity_ids.length > 0) {
    params.unity_ids.forEach((id) => {
      queryParams.append("unity_ids[]", id.toString());
    });
  }
  if (params?.status && params.status.length > 0) {
    params.status.forEach((s) => {
      queryParams.append("status[]", s);
    });
  }
  if (params?.type && params.type.length > 0) {
    params.type.forEach((t) => {
      queryParams.append("type[]", t);
    });
  }
  if (params?.requester_id && params.requester_id.length > 0) {
    params.requester_id.forEach((id) => {
      queryParams.append("requester_id[]", id.toString());
    });
  }
  if (params?.sale_id) {
    queryParams.append("sale_id", params.sale_id.toString());
  }
  if (params?.client_name) {
    queryParams.append("client_name", params.client_name);
  }
  if (params?.start_date) {
    queryParams.append("start_date", params.start_date);
  }
  if (params?.end_date) {
    queryParams.append("end_date", params.end_date);
  }
  if (params?.order_by) {
    queryParams.append("order_by", params.order_by);
  }
  if (params?.order_direction) {
    queryParams.append("order_direction", params.order_direction);
  }

  return apiAuth.get(`sale-change-requests?${queryParams.toString()}`);
};

type ExportPdfParams = {
  unity_ids?: number[];
  status?: string[];
  type?: string[];
  requester_id?: number[];
  sale_id?: number;
  client_name?: string;
  start_date?: string;
  end_date?: string;
};

const buildExportQuery = (params?: ExportPdfParams) => {
  const queryParams = new URLSearchParams();
  params?.unity_ids?.forEach((id) => queryParams.append("unity_ids[]", id.toString()));
  params?.status?.forEach((s) => queryParams.append("status[]", s));
  params?.type?.forEach((t) => queryParams.append("type[]", t));
  params?.requester_id?.forEach((id) => queryParams.append("requester_id[]", id.toString()));
  if (params?.sale_id) queryParams.append("sale_id", params.sale_id.toString());
  if (params?.client_name) queryParams.append("client_name", params.client_name);
  if (params?.start_date) queryParams.append("start_date", params.start_date);
  if (params?.end_date) queryParams.append("end_date", params.end_date);
  return queryParams.toString();
};

// Valida se as solicitações filtradas cabem em um PDF (total x limite)
export const checkSaleChangeRequestsPdf = (params?: ExportPdfParams) =>
  apiAuth.get(
    `sale-change-requests/export-pdf/check?${buildExportQuery(params)}`
  ) as apiResponseType;

// PDF das solicitações que batem nos filtros (todas, não só a página atual)
export const exportSaleChangeRequestsPdf = (params?: ExportPdfParams): Promise<Blob | null> =>
  apiAuth
    .get(`sale-change-requests/export-pdf?${buildExportQuery(params)}`, { responseType: "blob" })
    .then((response: any) => (response?.data instanceof Blob ? response.data : null));

// Detalhes de uma solicitação
export const getSaleChangeRequestShow = (uuid: string) =>
  apiAuth.get(`sale-change-requests/${uuid}/show`) as apiResponseType;

// Motivos disponíveis
export const getSaleChangeRequestReasons = (type?: "cancel" | "edit") => {
  const url = type
    ? `sale-change-requests/reasons?type=${type}`
    : "sale-change-requests/reasons";
  return apiAuth.get(url) as apiResponseType;
};

// Minhas solicitações (vendedor)
export const getMySaleChangeRequests = (
  page = 1,
  params?: {
    status?: string;
    type?: string;
  }
): apiResponseType => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());

  if (params?.status) {
    queryParams.append("status", params.status);
  }
  if (params?.type) {
    queryParams.append("type", params.type);
  }

  return apiAuth.get(`sale-change-requests/my-requests?${queryParams.toString()}`);
};

// Verificar se há solicitação pendente para uma venda
export const hasPendingSaleChangeRequest = (saleId: number) =>
  apiAuth.get(`sale-change-requests/has-pending/${saleId}`) as apiResponseType;

// Quantidade de solicitações pendentes
export const getPendingSaleChangeRequestCount = (unityIds: number[]) => {
  const queryParams = new URLSearchParams();
  unityIds.forEach((id) => {
    queryParams.append("unity_ids[]", id.toString());
  });
  return apiAuth.get(`sale-change-requests/pending-count?${queryParams.toString()}`) as apiResponseType;
};

// Criar solicitação de cancelamento
export const createCancelRequest = (data: {
  sale_uuid: string;
  reason_id?: number;
  reason_other?: string;
}) => apiAuth.post("sale-change-requests/cancel", data) as apiResponseType;

// Criar solicitação de cancelamento de pré-venda pendente
export const createCancelPreSaleRequest = (data: {
  sale_uuid: string;
  reason_id?: number;
  reason_other?: string;
}) => apiAuth.post("sale-change-requests/cancel-pre-sale", data) as apiResponseType;

// Criar solicitação de edição
export const createEditRequest = (data: {
  sale_uuid: string;
  reason_id?: number;
  reason_other?: string;
  sale_type_id?: number;
  circuit_route_id?: number;
  payment_forms?: Array<{
    payment_form_id: number;
    value: number;
    payment_advance_id?: number;
  }>;
  address?: {
    addressName: string;
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    state: string;
    zip_code: string;
    latitude: number | null;
    longitude: number | null;
    reference?: string;
  };
}) => apiAuth.post("sale-change-requests/edit", data) as apiResponseType;

// Aprovar solicitação
export const approveSaleChangeRequest = (
  uuid: string,
  data?: { reviewer_notes?: string }
) => apiAuth.post(`sale-change-requests/${uuid}/approve`, data || {}) as apiResponseType;

// Rejeitar solicitação
export const rejectSaleChangeRequest = (
  uuid: string,
  data?: { reviewer_notes?: string }
) => apiAuth.post(`sale-change-requests/${uuid}/reject`, data || {}) as apiResponseType;
